import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  AwLoginComponent,
  LoginFormData,
  AwSpinnerComponent,
  AwToastComponent,
  ToastVariants,
  ForgotPasswordData,
  ResetPasswordData,
  CustomFormData,
} from '@assetworks-llc/aw-component-lib';

import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';

import {
  AuthenticationResult,
  InteractionStatus,
  RedirectRequest,
  EventMessage,
  EventType,
} from '@azure/msal-browser';
import { of, Subject } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TokenManagerService } from '../../../../core/services/token-manager.service';
import { AuthErrorService } from '../../../../core/services/auth-error.service';
import { SingleSignOnService } from '../../../../core/services/single-sign-on.service';
import { GetSsoConfigurationRequest } from '../../../../api/models';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { UserService } from '../../../../core/services/user.service';
import { CookieService } from 'ngx-cookie-service';

@Component({
  selector: 'app-user-login',
  imports: [
    CommonModule,
    AwLoginComponent,
    AwSpinnerComponent,
    AwToastComponent,
  ],
  templateUrl: './user-login.component.html',
  styleUrl: './user-login.component.scss',
  standalone: true,
})
export class UserLoginComponent implements OnInit, OnDestroy {
  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService, // For triggering logins
    private msalBroadcastService: MsalBroadcastService, // Emits login events (login success, account added, token generated, etc.)
    private router: Router,
    private tokenManagerService: TokenManagerService,
    private userService: UserService,
    private authErrorService: AuthErrorService,
    private ssoService: SingleSignOnService,
    private cookieService: CookieService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  isIframe: boolean = false;
  isLoading: boolean = false;
  private readonly _destroying$ = new Subject<void>();

  formData: LoginFormData = {
    controls: [
      {
        controlRequired: false,
        formControlName: 'email',
        label: 'Integration Partner Email',
        value: '',
        icon: 'profile',
        type: 'text',
        validators: {
          required: {
            value: true,
            message: 'Email is required',
          },
        },
      },
    ],
  };

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  loginEmployee(
    formValues: ForgotPasswordData | ResetPasswordData | CustomFormData
  ) {
    if ('customUserForm' in formValues && formValues.customUserForm) {
      const email: string = formValues.customUserForm.email;
      const ssoConfiguration: GetSsoConfigurationRequest = {
        emailAddress: email,
      };

      this.ssoService
        .getEmployeeSSO({ body: ssoConfiguration })
        .pipe(
          catchError((error) => {
            return of(null);
          }),
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe((ssoResponse) => {
          if (ssoResponse) {
            window.location.href = ssoResponse.redirectUrl || '';
          } else {
            this.buildToastMessage(
              'Failed to redirect to SSO login. Please try again.',
              'error'
            );
          }
        });
    } else {
      this.buildToastMessage('Invalid form submission', 'error');
    }
  }

  ngOnInit(): void {
    //msal auth flow
    this.msalService.handleRedirectObservable().subscribe();
    this.isIframe = window !== window.parent && !window.opener;
    this.msalService.instance.enableAccountStorageEvents();

    // Listen for scope errors from the guard
    this.authErrorService.error$
      .pipe(
        filter((error) => !!error), // Only process non-null errors
        takeUntil(this._destroying$)
      )
      .subscribe((errorMessage) => {
        this.isLoading = true;
        this.authErrorService.clearErrorMessage();
        setTimeout(() => {
          this.buildToastMessage(errorMessage!, 'error');
        }, 100);
      });

    // Maintains the loading state to true if user is still in the middle of login redirect
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.HandleRedirect),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.isLoading = true;
      });

    // Retrieves the access token from a successful MSAL login
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe({
        next: (response: EventMessage) => {
          this.isLoading = false;
          this.userService.setOrganization('assetworks-employee');

          const result = response.payload as AuthenticationResult;
          this.msalService.instance.setActiveAccount(result.account);
          this.tokenManagerService.setAccessToken(result.accessToken);

          setTimeout(() => this.router.navigate(['/dashboard']), 100);
        },
        error: () => {
          this.isLoading = false;
          this.msalService.logoutRedirect({ postLogoutRedirectUri: '/login' });
          this.buildToastMessage('Failed to Login. Please try again.', 'error');
        },
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe({
        next: (result) => {
          this.isLoading = false;
          const hasMsalAccount =
            this.msalService.instance.getAllAccounts().length > 0;

          // Only honor the integration-employee cookie if MSAL is NOT signed in
          if (!hasMsalAccount) {
            const employee_access_token =
              this.cookieService.get('securitymanager');
            if (employee_access_token) {
              this.userService.setOrganization('integration-employee');
              this.tokenManagerService.setAccessToken(employee_access_token);
              setTimeout(() => {
                this.router.navigate(['/dashboard'], { replaceUrl: true });
              });
            }
          }

          this.checkAndSetActiveAccount();
        },
        error: () => {
          this.isLoading = false;
          this.buildToastMessage(
            'Session is Expired. Please log in again.',
            'error'
          );
          this.userService.clearOrganization();
          this.tokenManagerService.clearAccessToken();
          setTimeout(() => {
            this.msalService.logoutRedirect({
              postLogoutRedirectUri: '/login',
            });
          }, 3000);
        },
      });
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      let [first] = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(first);
    }
  }

  msalLoginclick = () => {
    this.isLoading = true;

    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      setTimeout(() => this.msalService.loginRedirect(), 100);
    }
  };
  msalLoginclickbinded = this.msalLoginclick.bind(this);

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
